import { useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { onError } from '../../../utils/error.util';
import { User } from '../../../models/user.model';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import { deleteUser, getUsers, enableUser, disableUser, updateUserOrder } from '../../../services/users.service';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const LIMIT = 60;

function UserTable({
  data,
  handleShowDeleteModal,
  handleEnableUser,
  handleDisableUser,
  onReorder,
  isUpdatingOrder,
}: any) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (isUpdatingOrder) return; // Prevent drag end if update is in progress

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = data.findIndex((item: User) => item.username === active.id);
      const newIndex = data.findIndex((item: User) => item.username === over.id);
      onReorder(oldIndex, newIndex);
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <DndContext
          sensors={isUpdatingOrder ? [] : sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px"></TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SortableContext items={data.map((item: User) => item.username)} strategy={verticalListSortingStrategy}>
                {data.map((row: any) => (
                  <UserTableRow
                    key={row.username}
                    row={row}
                    handleShowDeleteModal={handleShowDeleteModal}
                    handleEnableUser={handleEnableUser}
                    handleDisableUser={handleDisableUser}
                    isUpdatingOrder={isUpdatingOrder}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </DndContext>
      </TableContainer>
    </>
  );
}

function UserTableRow({
  row,
  handleShowDeleteModal,
  handleEnableUser,
  handleDisableUser,
  isUpdatingOrder,
}: {
  row: User;
  handleShowDeleteModal: any;
  handleEnableUser: any;
  handleDisableUser: any;
  isUpdatingOrder?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.username,
    disabled: isUpdatingOrder,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : isUpdatingOrder ? 0.6 : 1,
  };

  const email = row.attributes.find((attr) => attr.name === 'email')?.value || row.username;
  const givenName = row.attributes.find((attr) => attr.name === 'given_name')?.value || '';
  const familyName = row.attributes.find((attr) => attr.name === 'family_name')?.value || '';
  const displayName = `${givenName} ${familyName}`.trim() || '-';
  const position = row.attributes.find((attr) => attr.name === 'custom:position')?.value || '-';

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell>
        <Box
          {...(isUpdatingOrder ? {} : attributes)}
          {...(isUpdatingOrder ? {} : listeners)}
          sx={{
            cursor: isUpdatingOrder ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px',
            opacity: isUpdatingOrder ? 0.5 : 1,
          }}
        >
          <DragIndicatorIcon color="action" fontSize="small" />
        </Box>
      </TableCell>
      <TableCell>
        <ReactRouterLink to={`/users/${encodeURIComponent(row.username)}`}>
          <span>{email}</span>
        </ReactRouterLink>
      </TableCell>
      <TableCell>{displayName}</TableCell>
      <TableCell>{position}</TableCell>
      <TableCell>
        <Chip
          label={row.userStatus}
          color={
            row.userStatus === 'CONFIRMED'
              ? 'success'
              : row.userStatus === 'FORCE_CHANGE_PASSWORD'
              ? 'warning'
              : 'default'
          }
          size="small"
        />
      </TableCell>
      <TableCell>
        <Chip label={row.enabled ? 'Active' : 'Inactive'} color={row.enabled ? 'success' : 'default'} size="small" />
      </TableCell>
      <TableCell>{row.userCreateDate ? new Date(row.userCreateDate).toLocaleDateString() : '-'}</TableCell>
      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {row.enabled ? (
            <Tooltip title="Click to deactivate user">
              <IconButton onClick={() => handleDisableUser(row.username)} size="small">
                <CheckCircleIcon color="success" fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Click to activate user">
              <IconButton onClick={() => handleEnableUser(row.username)} size="small">
                <RadioButtonUncheckedIcon color="error" fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <IconButton onClick={() => handleShowDeleteModal(row.username)} size="small">
              <DeleteIcon color="error" fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}

export default function UsersContainer() {
  const location = useLocation();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | undefined>(undefined);

  useEffect(() => {
    loadUsers();
  }, [location]);

  async function loadUsers() {
    try {
      setIsLoading(true);
      const result = await getUsers({ limit: LIMIT });
      // Sort by order if available, otherwise maintain current order
      const sortedItems = [...result.items].sort((a, b) => {
        const orderA = a.order ?? 999999;
        const orderB = b.order ?? 999999;
        return orderA - orderB;
      });
      setUsers(sortedItems);
    } catch (e: any) {
      onError(e);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleReorder(oldIndex: number, newIndex: number) {
    if (isUpdatingOrder) return; // Prevent reordering while update is in progress

    const reorderedUsers = arrayMove(users, oldIndex, newIndex);
    setUsers(reorderedUsers);

    // Update order for all items
    try {
      setIsUpdatingOrder(true);
      const updatePromises = reorderedUsers.map((item, index) => {
        return updateUserOrder(item.username, index);
      });
      await Promise.all(updatePromises);
    } catch (e: any) {
      onError(e);
      // Reload on error to restore original order
      loadUsers();
    } finally {
      setIsUpdatingOrder(false);
    }
  }

  function handleShowDeleteModal(username: string) {
    setUserToDelete(username);
    setIsDeleteModalOpen(true);
  }

  function handleCloseDeleteModal() {
    setIsDeleteModalOpen(false);
    setUserToDelete(undefined);
  }

  async function handleDelete() {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete);
      handleCloseDeleteModal();
      loadUsers();
    } catch (e: any) {
      onError(e);
    }
  }

  async function handleEnableUser(username: string) {
    try {
      await enableUser(username);
      loadUsers();
    } catch (e: any) {
      onError(e);
    }
  }

  async function handleDisableUser(username: string) {
    try {
      await disableUser(username);
      loadUsers();
    } catch (e: any) {
      onError(e);
    }
  }

  return (
    <>
      <main id="main">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <h1>Manage Users</h1>
              <ReactRouterLink to="/users/new">
                <Button variant="contained" color="primary">
                  Add New User
                </Button>
              </ReactRouterLink>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            {isLoading ? (
              <CircularProgressComponent />
            ) : (
              <UserTable
                data={users}
                handleShowDeleteModal={handleShowDeleteModal}
                handleEnableUser={handleEnableUser}
                handleDisableUser={handleDisableUser}
                onReorder={handleReorder}
                isUpdatingOrder={isUpdatingOrder}
              />
            )}
          </Grid>
        </Grid>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
